import type z from 'zod/v4'
import { relations } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { LibraryHomepageRowToBook } from './library-homepage-row-to-book'
import { LibraryHomepageRowToBookCollection } from './library-homepage-row-to-book-collection'

export const LibraryHomepageRow = pgTable(
  'library_homepage_row',
  {
    id: uuid().notNull().primaryKey(),
    title: text().notNull(),
    order: integer().notNull(),
    isActive: boolean().notNull().default(true),
    contentType: text().notNull(), // 'book' | 'book-collection'
    type: text().notNull(), // 'preset' | 'custom'
    presetType: text('preset_type'), // 'newest-book' | 'most-liked-book' | etc. (nullable for custom)
    limit: integer(), // nullable for custom
    updatedAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
  },
  (columns) => [
    index('library_homepage_row_order_idx').on(columns.order),
    index('library_homepage_row_is_active_idx').on(columns.isActive),
    index('library_homepage_row_content_type_idx').on(columns.contentType),
    index('library_homepage_row_type_idx').on(columns.type),
    index('library_homepage_row_updated_at_idx').on(columns.updatedAt),
    index('library_homepage_row_created_at_idx').on(columns.createdAt),
  ],
)

export const libraryHomepageRowRelations = relations(LibraryHomepageRow, ({ many }) => ({
  libraryHomepageRowToBooks: many(LibraryHomepageRowToBook),
  libraryHomepageRowToBookCollections: many(LibraryHomepageRowToBookCollection),
}))

const BOOK_PRESETS = ['newest-book', 'most-liked-book', 'most-viewed-book'] as const
const COLLECTION_PRESETS = ['newest-collection', 'most-liked-collection', 'most-viewed-collection'] as const

export const InsertLibraryHomepageRowSchema = createInsertSchema(LibraryHomepageRow).refine(
  (data) => {
    // When type is 'preset', presetType MUST be present
    if (data.type === 'preset') {
      if (!data.presetType) {
        return false
      }

      // Validate that presetType matches contentType
      if (data.contentType === 'book') {
        return BOOK_PRESETS.includes(data.presetType as (typeof BOOK_PRESETS)[number])
      } else if (data.contentType === 'book-collection') {
        return COLLECTION_PRESETS.includes(data.presetType as (typeof COLLECTION_PRESETS)[number])
      }
    }
    return true
  },
  {
    message: 'Preset type must be valid and match the content type',
    path: ['presetType'],
  },
)

export const UpdateLibraryHomepageRowSchema = createUpdateSchema(LibraryHomepageRow)
  .omit({ id: true })
  .partial()
  .refine(
    (data) => {
      // Skip validation if type is not being updated
      if (!data.type) {
        return true
      }

      // When type is 'preset', presetType MUST be present if contentType is also provided
      if (data.type === 'preset') {
        if (data.contentType) {
          if (!data.presetType) {
            return false
          }

          // Validate that presetType matches contentType
          if (data.contentType === 'book') {
            return BOOK_PRESETS.includes(data.presetType as (typeof BOOK_PRESETS)[number])
          } else if (data.contentType === 'book-collection') {
            return COLLECTION_PRESETS.includes(data.presetType as (typeof COLLECTION_PRESETS)[number])
          }
        }
      }
      return true
    },
    {
      message: 'Preset type must be valid and match the content type',
      path: ['presetType'],
    },
  )
export const SelectLibraryHomepageRowSchema = createSelectSchema(LibraryHomepageRow).refine(
  (data) => {
    // When type is 'preset', presetType MUST be present
    if (data.type === 'preset') {
      if (!data.presetType) {
        return false
      }

      // Validate that presetType matches contentType
      if (data.contentType === 'book') {
        return BOOK_PRESETS.includes(data.presetType as (typeof BOOK_PRESETS)[number])
      } else if (data.contentType === 'book-collection') {
        return COLLECTION_PRESETS.includes(data.presetType as (typeof COLLECTION_PRESETS)[number])
      }
    }
    return true
  },
  {
    message: 'Preset type must be valid and match the content type',
    path: ['presetType'],
  },
)

export type InsertLibraryHomepageRow = typeof LibraryHomepageRow.$inferInsert
export type UpdateLibraryHomepageRow = z.infer<typeof UpdateLibraryHomepageRowSchema>
export type SelectLibraryHomepageRow = typeof LibraryHomepageRow.$inferSelect
